import { FeatureFlag, isEnabled } from '@client/monolith/legacy/services/features';
import store from '@client/monolith/legacy/store';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

/** Who the agent is serving. Mirrors the plugin's own `AgentAudience`. */
export type AgentAudience = 'retailer' | 'brand';

/**
 * Shop routes the retailer agent is offered on. Unchanged — a hardcoded list
 * until another is needed.
 */
const RETAILER_ROUTES = [
  'connected',
  'search',
  'boutique',
  'category',
  'brand',
  'product',
  'mylist',
  'collection',
  'catalogEvent',
  'new',
];

/**
 * The brand back-office is a single `/account` parent with everything nested
 * beneath it, so a path prefix covers all of it — around forty route names that
 * would otherwise have to be listed here and kept in sync.
 *
 * Retailers also have `/account` pages; this only decides where the *brand*
 * agent appears, and the audience is resolved from the account, not the path.
 */
const BRAND_PATH_PREFIX = '/account';

export const useAgent = () => {
  const route = useRoute();

  const isLoggedIn = computed(() => store.getters['isUserAuthenticated']);

  const audience = computed<AgentAudience>(() =>
    store.getters['userIsBrand'] ? 'brand' : 'retailer'
  );

  /**
   * The brand assistant, offered from a button in the brand back-office header.
   *
   * Behind its own kill switch so it can be shipped or stopped without touching
   * the live retailer agent.
   */
  const brandAssistantEnabled = computed(
    () =>
      isLoggedIn.value &&
      audience.value === 'brand' &&
      !isEnabled(FeatureFlag.DisableBrandAiAssistant) &&
      route.path.startsWith(BRAND_PATH_PREFIX)
  );

  /**
   * The retailer agent, opened from the search bar. Unchanged.
   *
   * Brands are excluded outright: they get the header button instead, and
   * without this exclusion the globally mounted wrapper would give them a second
   * trigger floating over every page.
   */
  const agentEnabled = computed(
    () =>
      isLoggedIn.value &&
      audience.value === 'retailer' &&
      isEnabled('search-ai-plugin') &&
      RETAILER_ROUTES.includes(route.name as string)
  );

  return {
    agentEnabled,
    brandAssistantEnabled,
    audience,
  };
};
